import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';

import { List, ListItemButton, Paper, Typography } from '@mui/material';

import macOsScrollBar from 'main/theme/mac-os-scrollbar';
import ProtectedData, { isProtectedData, LockIconBadge } from 'pages/item/v2/components/ProtectedData';
import isEmpty from 'utils/is-empty';
import Utils from 'utils/Utils';

const useStyles = makeStyles({ name: 'References' })((theme) => ({
    head: {
        fontSize: 24,
        fontWeight: 700,
        marginBottom: 30,
        marginTop: 60
    },
    paper: {
        background: '#FAFAFA',
        maxHeight: 300,
        padding: '8px 20px',
        ...macOsScrollBar({ my: 18, mx: 8, visible: 'always' })
    },
    list: {
        display: 'flex',
        flexDirection: 'column'
    },
    listItem: {
        padding: '12px 0',
        borderBottom: '1px solid #EEEEEE',
        '&:last-child': {
            borderBottom: 'none'
        }
    },
    listItemText: {
        color: theme.palette.primary.main,
        whiteSpace: 'pre',
        overflowX: 'hidden',
        textOverflow: 'ellipsis'
    }
}));

interface Props {
    references: string[];
}

const References: React.FC<Props> = ({ references }) => {
    const { classes } = useStyles();

    const hasProtectedData = useMemo(() => references?.some((item) => isProtectedData(item)), [references]);

    const sortedReferences = useMemo(() => references?.slice().sort((...args) => Utils.sortByNumberOrString(...args)), [
        references
    ]);

    if (isEmpty(references)) {
        return null;
    }

    return (
        <div>
            <Typography className={classes.head} variant="h2">
                <LockIconBadge invisible={!hasProtectedData}>{'References'}</LockIconBadge>
            </Typography>
            <Paper className={classes.paper} elevation={0}>
                <List className={classes.list}>
                    {sortedReferences.map((item, index) => (
                        <ProtectedData data={item} key={index}>
                            {({ isProtected, content }) => (
                                <ListItemButton
                                    className={classes.listItem}
                                    {...(isProtected ? {} : { component: 'a', href: content, target: '_blank' })}
                                >
                                    <Typography variant="body2" className={classes.listItemText}>
                                        {content.replace('https://', '').replace('http://', '')}
                                    </Typography>
                                </ListItemButton>
                            )}
                        </ProtectedData>
                    ))}
                </List>
            </Paper>
        </div>
    );
};

export default References;
